import { useState, type ReactNode } from 'react';

import Autocomplete, { type AutocompleteItem, type AutocompleteProps } from '@/components/forms/autocomplete';
import Select, { type SelectProps } from '@/components/forms/select';

/** `N` est le nom du champ : le `setValue` appelant doit accepter une chaîne ou `null`. */
export interface FilteredAutocompleteProps<N extends string = string> {
    name: N;
    label?: ReactNode;
    /** Libellé de l'option qui bascule sur l'autocomplétion de toute la liste. */
    labelothervalue?: string;
    /** Liste complète, proposée en autocomplétion après le choix « Autre ». */
    data: AutocompleteItem[];
    /** Sous-liste proposée d'abord dans la liste déroulante. */
    datafiltered: AutocompleteItem[];
    setValue: (name: N, value: string | null) => void;
    /** Reçoit la valeur choisie dans la liste déroulante (hors « Autre »). */
    onChange?: (value: string) => void;
    error?: AutocompleteProps['error'];
    required?: boolean;
    value?: SelectProps['value'];
    defaultValue?: string;
}

const FilteredAutocomplete = <N extends string>(props: FilteredAutocompleteProps<N>) => {
    const { labelothervalue = 'Autre', datafiltered, data, setValue, name, onChange = () => {} } = props;
    const [showAll, setShowAll] = useState(false);

    return !showAll ? (
        <Select
            // Comme avant, toutes les props sont transmises à la liste déroulante.
            {...(props as unknown as SelectProps)}
            name={`filtered${name}`}
            onChange={(e) => {
                setValue(name, e.target.value === 'other' ? null : e.target.value);

                if (e.target.value === 'other') {
                    setShowAll(true);
                } else {
                    onChange(e.target.value);
                }
            }}
        >
            <option value={undefined}>Choisissez une valeur</option>
            {datafiltered.map((c, index) => (
                <option key={index} value={c.value}>
                    {c.label}
                </option>
            ))}
            <option value={'other'}>{labelothervalue}</option>
        </Select>
    ) : (
        <Autocomplete {...(props as unknown as AutocompleteProps<N>)} data={data} setValue={setValue} />
    );
};

export default FilteredAutocomplete;
